import { cn } from '@/lib/utils'
import type { EvaluationGrade } from '@/types/common'

interface BadgeBaseProps {
  /** 추가 CSS 클래스 */
  className?: string
}

interface GradeBadgeProps extends BadgeBaseProps {
  /** 배지 종류: 등급 */
  variant: 'grade'
  /** 평가 등급 (A~E) */
  grade: EvaluationGrade
  children?: never
}

interface TypeBadgeProps extends BadgeBaseProps {
  /** 배지 종류: 유형 (요양원, 주간보호 등) */
  variant: 'type'
  grade?: never
  children: React.ReactNode
}

interface CertifiedBadgeProps extends BadgeBaseProps {
  /** 배지 종류: 인증 후기 */
  variant: 'certified'
  grade?: never
  children?: never
}

interface PremiumBadgeProps extends BadgeBaseProps {
  /** 배지 종류: 프리미엄 시설 */
  variant: 'premium'
  /** 구독 플랜 (PREMIUM 단일) — 하위 호환용으로 유지되며 현재는 표기에 영향 없음 */
  plan?: 'PREMIUM'
  grade?: never
  children?: never
}

type BadgeProps = GradeBadgeProps | TypeBadgeProps | CertifiedBadgeProps | PremiumBadgeProps

/** 등급별 컬러 스타일 (보더 추가로 선명한 구분감) */
const gradeStyles: Record<EvaluationGrade, string> = {
  A: 'bg-cb-grade-a-bg text-cb-grade-a border border-[#38D9A9] font-semibold',
  B: 'bg-cb-grade-b-bg text-cb-grade-b border border-[#74C0FC] font-semibold',
  C: 'bg-cb-grade-c-bg text-cb-grade-c border border-[#FFD43B] font-semibold',
  D: 'bg-cb-grade-d-bg text-cb-grade-d border border-[#FF922B] font-semibold',
  E: 'bg-cb-grade-e-bg text-cb-grade-e border border-[#FF6B6B] font-semibold',
}

/** 등급별 라벨 */
const gradeLabels: Record<EvaluationGrade, string> = {
  A: 'A등급',
  B: 'B등급',
  C: 'C등급',
  D: 'D등급',
  E: 'E등급',
}

/**
 * 배지 컴포넌트.
 *
 * - grade: 평가 등급 (A=초록, B=파랑, C=노랑, D=주황, E=빨강)
 * - type: 시설 유형 (요양원, 주간보호 등)
 * - certified: 인증 후기 마크
 */
export default function Badge(props: BadgeProps) {
  const { variant, className } = props

  const baseStyles =
    'inline-flex items-center gap-1 rounded-[var(--radius-badge)] px-2 py-0.5 text-caption font-medium'

  if (variant === 'grade') {
    return (
      <span
        className={cn(baseStyles, gradeStyles[props.grade], className)}
        aria-label={`평가 등급 ${props.grade}`}
      >
        {gradeLabels[props.grade]}
      </span>
    )
  }

  if (variant === 'premium') {
    // 2-tier 재편: 단일 PREMIUM 플랜만 존재 (기존 SILVER/GOLD 제거)
    return (
      <span
        className={cn(
          baseStyles,
          'bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 border border-blue-300 font-semibold',
          className
        )}
        aria-label="프리미엄"
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path d="M6 1L7.5 4.5L11 5L8.5 7.5L9 11L6 9.5L3 11L3.5 7.5L1 5L4.5 4.5L6 1Z"
            fill="currentColor" />
        </svg>
        프리미엄
      </span>
    )
  }

  if (variant === 'certified') {
    return (
      <span
        className={cn(
          baseStyles,
          'bg-cb-secondary-light text-cb-secondary',
          className
        )}
        aria-label="인증 후기"
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M10 3L4.5 8.5L2 6"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        인증 후기
      </span>
    )
  }

  // variant === 'type'
  return (
    <span
      className={cn(
        baseStyles,
        'bg-cb-gray-100 text-cb-gray-600',
        className
      )}
    >
      {props.children}
    </span>
  )
}
